import { FC } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import { ActivityType } from '@forto-dev/feed-v2-models'
import { Card, FeedItem, Table } from '@forto-dev/phoenix-ui-core'

import { houseShipmentByCodeQueryOptions } from '@/features/shipments'
import { useLocalisation } from '@/utils/localisation'

import { ActivityFeedItemProps } from './types'
import { FeedItem as FeedItemType } from '../domain/feed'

const allowedActivityTypes = {
  [ActivityType.ChangeCargoReadyDate]: true,
  [ActivityType.ChangeCargoReadyDateAggregation]: true,
  [ActivityType.ChangePickup]: true,
  [ActivityType.ChangePickupAggregation]: true,
  [ActivityType.ChangeEstimatedTimeDeparture]: true,
  [ActivityType.ChangeEstimatedTimeDepartureAggregation]: true,
  [ActivityType.ChangeArrival]: true,
  [ActivityType.ChangeArrivalAggregation]: true,
  [ActivityType.ChangeDelivery]: true,
  [ActivityType.ChangeDeliveryAggregation]: true,
} as const
const isAllowedActivityType = (activityType: ActivityType): activityType is keyof typeof allowedActivityTypes =>
  activityType in allowedActivityTypes
const useTransportPlanActivityTableData = (feed: FeedItemType) => {
  const { translateDate } = useLocalisation()
  const updatedDate = feed.activity.customField.date

  const updatedFormattedDate = updatedDate
    ? updatedDate.isDateTime
      ? translateDate('feed_date', updatedDate.value)
      : translateDate('feed_date_only', updatedDate.value)
    : undefined

  return {
    updatedFormattedDate,
  }
}

const getContainerLevelTableData = (
  feed: FeedItemType,
  updatedFormattedDate: string | undefined,
  houseCargo: HouseCargo[]
) => {
  const isAggregate = feed.activity.aggregation?.role === 'aggregate'

  const columns = [
    {
      accessorKey: 'date',
      header: 'Date',
    },
    {
      accessorKey: 'containerNumbers',
      header: 'Container Numbers',
    },
  ]
  const trackables = feed.activity.object.trackables || []
  const rows = isAggregate
    ? [
        {
          date: updatedFormattedDate,
          containerNumbers:
            trackables
              .map((item) => item?.containerNumber)
              .filter((item) => item) // remove undefined
              .join(' | ') || '-',
        },
      ]
    : (trackables.map((trackable) => {
        const cargoContainerNumber = houseCargo?.find((container) => container?.ID === trackable?.id)?.containerNumber
        return {
          date: updatedFormattedDate,
          containerNumbers: cargoContainerNumber ?? trackable?.containerNumber ?? '-',
        }
      }) ?? [])

  return { columns, rows }
}

const getShipmentLevelTableData = (feed: FeedItemType, updatedFormattedDate: string | undefined) => {
  const reason = feed.activity.customField.reason
  return {
    columns: [
      {
        accessorKey: 'date',
        header: 'Date',
      },
      ...(reason
        ? [
            {
              accessorKey: 'reason',
              header: 'Reason',
            },
          ]
        : []),
    ],
    rows: [{ date: updatedFormattedDate, reason }],
  }
}

type HouseCargo = { ID: string; containerNumber?: string | undefined }

const getTableData = (feed: FeedItemType, updatedFormattedDate: string | undefined, houseCargo: HouseCargo[]) => {
  if (feed.activity.object.trackables) {
    return getContainerLevelTableData(feed, updatedFormattedDate, houseCargo)
  }
  return getShipmentLevelTableData(feed, updatedFormattedDate)
}
export const ActivityFeedItemChangeTimestamp: FC<ActivityFeedItemProps> = ({ item }) => {
  const { translate, translateDate } = useLocalisation()
  const shipment = useSuspenseQuery(houseShipmentByCodeQueryOptions(item.feedInfo.entityID))
  const activityType = item.activity.type
  const { updatedFormattedDate } = useTransportPlanActivityTableData(item)
  if (!isAllowedActivityType(activityType)) {
    return null
  }

  const { columns, rows } = getTableData(item, updatedFormattedDate, shipment.data.houseCargos)
  return (
    <FeedItem
      type="message"
      title={
        item.activity.customField.actual
          ? translate(`feed.list.${activityType}.title.actual`)
          : item.activity.customField.confirmed
            ? translate(`feed.list.${activityType}.title.confirmed`)
            : translate(`feed.list.${activityType}.title.estimated`)
      }
      date={translateDate('feed_date', item.activity.createdAt)}
      suffix={item.activity.actor.type === 'Service' ? translate('feed.suffix.automated') : undefined}
    >
      {item.activity.customField.date ? (
        <Card>
          <Table
            columns={columns}
            data={rows as { date?: string; reason?: string; containerNumbers?: string }[]}
            footless
          />
        </Card>
      ) : null}
    </FeedItem>
  )
}
